import { NgIf } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { FormHelper } from '@common/form-helper';
import { Month } from '@common/month';
import { Months } from '@common/months';
import { scrollToFirstInvalidControl } from '@common/scroll-to-first-invalid-control';
import { ChooseAPharmacyComponent } from '@components/consultation-request/choose-a-pharmacy/choose-a-pharmacy.component';
import { ConsultationRequestMedicalHistoryComponent } from '@components/consultation-request/consultation-request-medical-history/consultation-request-medical-history.component';
import { ConsultationRequestPaymentInfoComponent } from '@components/consultation-request/consultation-request-payment-info/consultation-request-payment-info.component';
import { ContactInformationComponent } from '@components/consultation-request/contact-information/contact-information.component';
import { PaymentSubmitButtonComponent } from '@components/consultation-request/payment-submit-button/payment-submit-button.component';
import { PersonalInformationComponent } from '@components/consultation-request/personal-information/personal-information.component';
import { PrescriptionInformationComponent } from '@components/consultation-request/prescription-information/prescription-information.component';
import { TermsOfServiceComponent } from '@components/consultation-request/terms-of-service/terms-of-service.component';
import { ConsultationStatus } from '@enums/consultation-status';
import { ConsultationStorageKeys } from '@enums/consultation-storage-keys';
import { ConsultationTreatmentTypes } from '@enums/consultation-treatment-types';
import { Gender } from '@enums/gender';
import { QuestionnaireAnswers } from '@enums/questionnaire-answers';
import { Treatment } from '@enums/treatment';
import { environment } from '@environments/environment';
import { ConsentAgreementRequiredError } from '@errors/consent-agreement-required-error';
import { IncompleteOrderFormError } from '@errors/incomplete-order-form-error';
import { InternationalCreditCardNotSupportedError } from '@errors/international-credit-card-not-supported-error';
import { PharmacyRequiredError } from '@errors/pharmacy-required-error';
import { OrderDetailsUpdate } from '@interfaces/order-details-update';
import { ConfigService, FreePayment, Payment, PaymentTypes } from '@medology/ngx-payment';
import { BasicConsultationRequestResponse } from '@models/consultation-request/basic-consultation-request-response';
import { ConsultationRequestAdditionalInfo } from '@models/consultation-request/consultation-request-additional-info';
import { ConsultationRequestFormRequest } from '@models/consultation-request/consultation-request-form-request';
import { ConsultationRequestHelper } from '@models/consultation-request/consultation-request-helper';
import { Patient } from '@models/patient';
import { PaymentRechargeBraintreeStoreRequest } from '@models/requests/payment-recharge-briantree-store-request.model';
import { TransactionResponse } from '@models/transaction-response';
import { ConsultationRequestService } from '@services/consultation-request.service';
import { ConsultationStorageService } from '@services/consultation-storage.service';
import { DataLayerService } from '@services/data-layer.service';
import { ErrorHandlerService } from '@services/error-handler.service';
import { ErrorLoggingService } from '@services/error-logging.service';
import { NavigationService } from '@services/navigation.service';
import { OrderService } from '@services/order.service';
import { OrderPaymentService } from '@services/order-payment.service';
import { PaymentService, provideDefaultPaymentConfig } from '@services/payment.service';
import { catchError, concatMap, map, Observable, of, Subscription, switchMap, take, tap } from 'rxjs';

@Component({
  selector: 'app-std-consultation-request-form',
  templateUrl: './std-consultation-request-form.component.html',
  encapsulation: ViewEncapsulation.None,
  imports: [
    PersonalInformationComponent,
    ContactInformationComponent,
    ConsultationRequestMedicalHistoryComponent,
    NgIf,
    PrescriptionInformationComponent,
    ChooseAPharmacyComponent,
    ConsultationRequestPaymentInfoComponent,
    TermsOfServiceComponent,
    PaymentSubmitButtonComponent,
  ],
  providers: [provideDefaultPaymentConfig()],
})
export class StdConsultationRequestFormComponent extends FormHelper implements OnInit, OnDestroy {
  @ViewChild('consultationRequestPaymentInfo', { static: false })
  public consultationRequestPaymentInfo: ConsultationRequestPaymentInfoComponent;
  public calendarOptions: { months: Month[]; days: number[]; years: number[] };
  public formGroupsToBeValidated = ['personal', 'contact', 'medicalHistory', 'prescription'];
  public payments: Payment[] = [];
  public isFreeSelected: boolean = false;
  public formValidationError: Error | null = null;
  public submissionErrors: (string | Error)[] = [];
  public processingOrder: boolean = false;

  protected consultationRequestService = inject(ConsultationRequestService);

  private paymentService = inject(PaymentService);
  private configService = inject(ConfigService);
  private activatedRoute = inject(ActivatedRoute);
  private dataLayerService = inject(DataLayerService);
  private errorHandlerService = inject(ErrorHandlerService);
  private consultationStorageService = inject(ConsultationStorageService);
  private errorLoggingService = inject(ErrorLoggingService);
  private navigationService = inject(NavigationService);
  private router = inject(Router);
  private orderPaymentService = inject(OrderPaymentService);
  private orderService = inject(OrderService);
  private params: Params;
  private subscriptions: Subscription[] = [];
  private userId: number = null;
  private isFormValidationActive: boolean = false;
  private formValidationSubscription: Subscription | null = null;

  /**
   * Get the FormGroup for the pharmacy section
   */
  public get pharmacyForm(): FormGroup {
    return this.consultationRequestService.consultationForm.get('pharmacy') as FormGroup;
  }

  /**
   * Determines if the treatment type is STD.
   *
   * @returns {boolean} true if the treatment type is STD, false otherwise
   */
  public get isStdTreatmentType(): boolean {
    return this.consultationStorageService.treatmentType === ConsultationTreatmentTypes.Std;
  }

  /**
   * Gets the text to display on the place order button.
   */
  public get submitButtonText(): string {
    return environment.buttons.checkout;
  }

  /**
   * Gets the current consultation order hash.
   *
   * @return {string} the current consultation order hash
   */
  private get hash(): string {
    return this.consultationRequestService.consultationOrderDetail.hash;
  }

  /**
   * Gets the current consultation order transaction ID.
   *
   * @return {string} the current consultation order transaction ID
   */
  private get transactionId(): string {
    return this.consultationRequestService.consultationOrderDetail.transaction_id;
  }

  /**
   * Initializes the component.
   */
  public ngOnInit(): void {
    this.consultationStorageService.remove(ConsultationStorageKeys.ScheduleOnceId, false);
    this.consultationStorageService.remove(ConsultationStorageKeys.ConsultationAdditionalInfo, false);
    this.setCalendarOptions();
    this.subscriptions.push(this.listenForQueryParamsChanges());
    this.checkManageUser();
  }

  /**
   * Unsubscribes from all subscriptions.
   */
  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  /**
   * Handles the payment submit button click.
   */
  public onPaymentSubmit(): void {
    this.submissionErrors = [];
    this.consultationRequestService.consultationForm.updateValueAndValidity();
    this.formValidationError = this.getFormValidationError();

    if (this.formValidationError) {
      this.setupFormValidationListener();
      scrollToFirstInvalidControl();

      return;
    }

    this.submitConsultationRequest();
  }

  /**
   * Validates the consultation request form and returns the first validation error found.
   *
   * @returns {Error | null} the validation error, or null if all validations pass
   */
  public getFormValidationError(): Error | null {
    const paymentFormValid = this.consultationRequestPaymentInfo?.validatePaymentForm();
    const termsControl = this.consultationRequestService.consultationForm.get('terms');
    const hasInvalidFormGroup = this.formGroupsToBeValidated
      .map((formGroupPath) => this.consultationRequestService.consultationForm.get(formGroupPath))
      .some((control) => !control.valid);

    if (hasInvalidFormGroup) {
      return new IncompleteOrderFormError();
    }

    if (this.pharmacyForm.invalid) {
      return new PharmacyRequiredError();
    }

    if (!paymentFormValid || !this.payments.length) {
      return new IncompleteOrderFormError();
    }

    if (termsControl && !termsControl.value) {
      return new ConsentAgreementRequiredError();
    }

    return null;
  }

  /**
   * Places a consultation request order.
   *
   * Processes the payment first, then creates the consultation request. The consultation request creation surfaces its
   * error to the user, while the follow-up requests (charges, discount, order details and customer note) swallow their
   * errors to avoid failing an order that has already been paid for and created.
   */
  public submitConsultationRequest(): void {
    if (this.paymentService.hasUnsupportedCreditCard(this.payments)) {
      this.submissionErrors = [new InternationalCreditCardNotSupportedError(environment.phone).message];

      return;
    }

    this.processingOrder = true;

    let consultationRequestResponse: BasicConsultationRequestResponse | null = null;

    this.processPayment()
      .pipe(
        concatMap(() => this.createConsultationRequest()),
        tap((response) => (consultationRequestResponse = response)),
        concatMap(() => this.addConsultationCharge()),
        concatMap(() => this.addPartnerCharge()),
        concatMap(() => this.addConsultationDiscount()),
        concatMap(() => this.updateOrderDetails()),
        concatMap(() => this.createCustomerNoteIfFree()),
      )
      .subscribe({
        next: () => this.handleConsultationRequestSuccess(consultationRequestResponse),
        error: (error) => {
          this.errorLoggingService.logError(error);
          this.handleConsultationRequestError(error);
        },
      });
  }

  /**
   * Updates the payment methods accordingly.
   *
   * @param {Payment[]} payments the selected payment methods emitted from the payment step tabs component
   */
  public onPaymentsSelected(payments: Payment[]): void {
    this.submissionErrors = [];

    // Run after change detection
    setTimeout(() => {
      this.payments = payments;
      this.isFreeSelected = payments.some((payment) => payment instanceof FreePayment);

      if (this.isFormValidationActive || this.paymentService.hasExternalPayment(this.payments)) {
        this.setupFormValidationListener();
      }
    });
  }

  /**
   * Re-evaluates the submit validation error once the payment form becomes fully valid.
   */
  public onPaymentFormValid(): void {
    if (!this.isFormValidationActive) {
      return;
    }

    this.formValidationError = this.getFormValidationError();
  }

  /**
   * Processes the payment for the consultation request.
   *
   * Skips payment when a free consultation is selected. Uses the stored Recharge vault when available, otherwise
   * submits the payment requests derived from the selected payment methods.
   *
   * @returns {Observable<TransactionResponse | null>} the payment transaction response, or null when no payment is made
   */
  private processPayment(): Observable<TransactionResponse | null> {
    if (this.isFreeSelected) {
      return of(null);
    }

    if (this.hasStoredRechargeVault()) {
      return this.storeRechargePayment();
    }

    return this.storeSelectedPayments();
  }

  /**
   * Determines whether a stored Recharge vault is available to charge against.
   *
   * @returns {boolean} true when a Recharge payment is selected and a stored vault ID exists
   */
  private hasStoredRechargeVault(): boolean {
    return (
      this.paymentService.hasRechargePayment(this.payments) && !!this.consultationStorageService?.recharge?.vault_id
    );
  }

  /**
   * Stores a payment using the customer's stored Recharge Braintree vault
   *
   * @returns {Observable<TransactionResponse | null>} the payment transaction response
   */
  private storeRechargePayment(): Observable<TransactionResponse | null> {
    return this.orderPaymentService.store(
      new PaymentRechargeBraintreeStoreRequest(
        this.transactionId,
        this.consultationRequestService.getTotal(),
        this.consultationStorageService.recharge.vault_id,
      ),
      this.hash,
    );
  }

  /**
   * Stores the payment requests derived from the selected payment methods
   *
   * @returns {Observable<TransactionResponse | null>} the payment transaction response
   */
  private storeSelectedPayments(): Observable<TransactionResponse | null> {
    const [initialRequest, followUpRequest] = this.paymentService.getPaymentRequests(
      this.payments,
      this.consultationRequestService.getTotal(),
      this.transactionId,
      `${this.consultationRequestService.consultationOrderDetail.first_name} ${this.consultationRequestService.consultationOrderDetail.last_name}`,
    );

    return this.orderPaymentService
      .store(initialRequest, this.hash)
      .pipe(
        switchMap((response) =>
          followUpRequest
            ? this.orderPaymentService
                .store(followUpRequest, this.hash)
                .pipe(catchError((error) => this.orderService.swallowError(error)))
            : of(response),
        ),
      );
  }

  /**
   * Creates the consultation request from the current form values.
   *
   * @returns {Observable<BasicConsultationRequestResponse | null>} the created consultation request response, or null
   * if the request fails
   */
  private createConsultationRequest(): Observable<BasicConsultationRequestResponse | null> {
    const consultationRequest = new ConsultationRequestFormRequest(
      this.consultationRequestService.consultationForm,
      this.transactionId,
      this.consultationStorageService.treatmentType,
      this.consultationRequestService.consultationOrderDetail.gender,
      this.consultationStorageService.recharge,
    );

    consultationRequest.consultation_status = this.isStdTreatmentType
      ? ConsultationStatus.Incomplete
      : ConsultationStatus.Pending;

    // For free (manage-user) orders we intentionally omit user_id
    if (this.userId && !this.isFreeSelected) {
      consultationRequest.user_id = this.userId;
    }

    return this.consultationRequestService.postBasicConsultationRequest(
      consultationRequest,
      this.hash,
      this.transactionId,
    );
  }

  /**
   * Stores a customer note when a free consultation is selected.
   *
   * @returns {Observable<void | null>} the result of storing the note, or null when no free consultation is selected
   */
  private createCustomerNoteIfFree(): Observable<void | null> {
    if (!this.isFreeSelected) {
      return of(null);
    }

    return this.orderService
      .storeCustomerNote(this.transactionId, this.hash, {
        customer_notes_comment: 'A free consultation was requested for this customer',
        customer_notes_attention_mark: 'NO',
        user_id: this.userId,
      })
      .pipe(catchError((error) => this.orderService.swallowError(error)));
  }

  /**
   * Adds the patient consultation request extra charge to the order.
   *
   * @returns {Observable<unknown>} the result of storing the extra charge
   */
  private addConsultationCharge(): Observable<unknown> {
    return this.orderService
      .storeExtraCharge(
        { name: 'Patient Consultation Request', amount: ConsultationRequestService.cost },
        this.transactionId,
        this.hash,
      )
      .pipe(catchError((error) => this.orderService.swallowError(error)));
  }

  /**
   * Adds the partner prescription extra charge to the order when the self-and-partner treatment is selected.
   *
   * @returns {Observable<unknown>} the result of storing the extra charge, or null when not a self-and-partner treatment
   */
  private addPartnerCharge(): Observable<unknown> {
    const isSelfAndPartner = this.consultationRequestService.treatmentControl.value === Treatment.SelfAndPartner;

    if (!isSelfAndPartner) {
      return of(null);
    }

    return this.orderService
      .storeExtraCharge(
        { name: 'Prescription for your partner', amount: ConsultationRequestService.cost },
        this.transactionId,
        this.hash,
      )
      .pipe(catchError((error) => this.orderService.swallowError(error)));
  }

  /**
   * Adds the consultation discount to the order when a discount applies.
   *
   * @returns {Observable<unknown>} the result of storing the discount, or null when no discount applies
   */
  private addConsultationDiscount(): Observable<unknown> {
    const discount = this.getApplicableDiscount();

    if (discount <= 0) {
      return of(null);
    }

    return this.orderService
      .storeDiscount(
        { name: 'Consultation Discount', amount: discount, user_id: this.userId },
        this.transactionId,
        this.hash,
      )
      .pipe(catchError((error) => this.orderService.swallowError(error)));
  }

  /**
   * Updates the order details with the contact and medical history information from the form.
   *
   * @returns {Observable<void>} the result of updating the order details
   */
  private updateOrderDetails(): Observable<void> {
    const form = this.consultationRequestService.consultationForm;
    const contact = form.get('contact').value;
    const medicalHistory = form.get('medicalHistory').value;

    const data: OrderDetailsUpdate = {
      phone: contact.phone,
      gender: this.consultationRequestService.consultationOrderDetail.gender,
      sex_with: medicalHistory.sex_with,
      symptoms: ConsultationRequestHelper.getSymptomsArrayFromValues(medicalHistory.symptoms).join(', '),
    };

    if (medicalHistory.pregnant) {
      data.pregnant = medicalHistory.pregnant;
    }

    if (medicalHistory.ethnicity) {
      data.ethnicity = medicalHistory.ethnicity;
    }

    if (medicalHistory.race) {
      data.race = medicalHistory.race;
    }

    if (medicalHistory.pregnant === QuestionnaireAnswers.Yes) {
      data.due_date = ConsultationRequestHelper.getDateDataString(medicalHistory.due_d);
    }

    if ([QuestionnaireAnswers.No, QuestionnaireAnswers.Unsure].includes(medicalHistory.pregnant)) {
      data.last_menstrual_date = ConsultationRequestHelper.getDateDataString(medicalHistory.menstrual_d);
    }

    return this.orderService
      .updateOrderDetails(this.transactionId, this.hash, data)
      .pipe(catchError((error) => this.orderService.swallowError(error)));
  }

  /**
   * Handles a successful consultation request submission by adding purchase on data layer and redirect to next page.
   *
   * @param {BasicConsultationRequestResponse} consultationRequestResponse the response from the consultation request
   */
  private handleConsultationRequestSuccess(consultationRequestResponse: BasicConsultationRequestResponse): void {
    this.processingOrder = false;
    this.addConsultationRequestPurchaseToDataLayer();
    this.prepareStorageForUpsells(consultationRequestResponse);
    this.navigateToNextPage();
  }

  /**
   * Navigates to the next page, using a two-step routing pattern for STD treatments to intentionally break browser
   * history and prevent backward navigation.
   */
  private navigateToNextPage(): void {
    if (this.isStdTreatmentType) {
      let nextUrl: string;

      this.navigationService
        .getConsultationRequestNextPageUrl(this.consultationStorageService.treatmentType)
        .then((url) => (nextUrl = url))
        .then(() => this.router.navigateByUrl('/consultation/navigation-restricted', { replaceUrl: true }))
        .then(() => this.router.navigateByUrl(nextUrl));

      return;
    }

    this.navigationService.navigateToNextConsultationRequestPage(this.consultationStorageService.treatmentType);
  }

  /**
   * Prepares storage for standard treatment upsells.
   *
   * @param {BasicConsultationRequestResponse} consultationRequestResponse the response from the newly created consultation request
   */
  private prepareStorageForUpsells(consultationRequestResponse: BasicConsultationRequestResponse): void {
    const consultationFormAnswers = this.consultationRequestService.consultationForm.value;
    const additionalInfo = new ConsultationRequestAdditionalInfo(
      consultationFormAnswers.prescription.partner,
      consultationFormAnswers.prescription.treatment,
      consultationFormAnswers.personal,
    );

    this.consultationRequestService.consultationOrderDetail.consultationRequest.id =
      consultationRequestResponse.id.toString();
    this.consultationRequestService.saveConsultationOrderDetailToLocalStorage();
    this.consultationStorageService.transactionId = this.transactionId;
    this.consultationStorageService.hash = this.hash;
    this.consultationStorageService.pharmacy = this.pharmacyForm.value;
    this.consultationStorageService.consultationAdditionalInfo = additionalInfo;
    this.consultationStorageService.treatmentFor = consultationFormAnswers.prescription.treatment;
    this.consultationStorageService.treatmentPreferences =
      this.consultationRequestService.getOrderedTreatmentPreferences();
    this.consultationStorageService.patient = this.getPatientFromAnswers(consultationFormAnswers);

    if (consultationRequestResponse?.schedule_once_id) {
      this.consultationStorageService.scheduleOnceId = consultationRequestResponse.schedule_once_id;
    }

    this.consultationStorageService.removeItemsWithSuffix('-consultation');
  }

  /**
   * Extracts patient information from consultation form answers.
   *
   * @param {any} consultationFormAnswers the consultation form answers
   *
   * @returns {Patient} the patient object with formatted information
   */
  private getPatientFromAnswers(consultationFormAnswers: any): Patient {
    return {
      name: consultationFormAnswers.personal.firstName,
      last_name: consultationFormAnswers.personal.lastName,
      email: consultationFormAnswers.contact.email,
      phone: consultationFormAnswers.contact.phone,
      birthday: consultationFormAnswers.personal.birthday,
      gender: <Gender>this.consultationRequestService.consultationOrderDetail.gender,
      height_feet: consultationFormAnswers.personal.height_feet,
      height_inches: consultationFormAnswers.personal.height_inches,
      weight: consultationFormAnswers.personal.weight,
    };
  }

  /**
   * Handles an error that occurred during the consultation request submission.
   *
   * @param {HttpErrorResponse} error the error that occurred during the consultation request submission
   */
  private handleConsultationRequestError(error: HttpErrorResponse): void {
    this.submissionErrors = this.errorHandlerService.handleResponseError(error);
    this.processingOrder = false;
  }

  /**
   * Adds the consultation request purchase to the data layer.
   */
  private addConsultationRequestPurchaseToDataLayer(): void {
    const cost = this.isFreeSelected ? 0 : ConsultationRequestService.cost;
    const isSelfAndPartner = this.consultationRequestService.treatmentControl.value === Treatment.SelfAndPartner;
    const discount = this.getApplicableDiscount();

    const lineItems: { name: string; price: number; type: string }[] = [
      { name: 'Patient Consultation Request', price: cost, type: 'Extra Charge' },
    ];

    if (isSelfAndPartner) {
      lineItems.push({ name: 'Prescription for your partner', price: cost, type: 'Extra Charge' });
    }

    if (discount > 0) {
      lineItems.push({ name: 'Consultation Discount', price: -discount, type: 'Discount' });
    }

    this.dataLayerService.addConsultationRequestPurchaseToDataLayer(
      {
        payment_amount: this.consultationRequestService.getTotal(),
        line_items: lineItems,
      },
      ConsultationTreatmentTypes.Std,
      this.transactionId,
    );
  }

  /**
   * Return the applicable discount. Full value when free otherwise the specific value.
   */
  private getApplicableDiscount(): number {
    return this.isFreeSelected
      ? this.consultationRequestService.getSubtotal()
      : this.consultationRequestService.getDiscount();
  }

  /**
   * Sets the date of birth select options.
   */
  private setCalendarOptions(): void {
    this.calendarOptions = {
      months: Months,
      days: this.getDays(),
      years: this.getYears(),
    };
  }

  /**
   * Gets a list of all the days
   *
   * @returns {number[]} a list of numbers from 1 to 31
   */
  private getDays(): number[] {
    return this.getListOfNumbers(1, 31);
  }

  /**
   * Gets a list of the last 90 years.
   *
   * @returns {number[]} a list of numbers from the current year to 1920
   */
  private getYears(): number[] {
    const currentYear = new Date().getFullYear();

    return this.getListOfNumbers(currentYear, currentYear - environment.maxAgeAllowed);
  }

  /**
   * Listens for changes in URL query params to set the userId variable.
   *
   * @returns {Subscription} a Subscription to changes in the URL query params
   */
  private listenForQueryParamsChanges(): Subscription {
    return this.activatedRoute.queryParams.subscribe((params) => (this.params = params));
  }

  /**
   * Enables free order if the manage ID provided is valid.
   */
  private checkManageUser(): void {
    const manageUserId = this.getManagerUserId();

    if (!manageUserId) {
      return;
    }

    this.validateUserId(manageUserId)
      .pipe(take(1))
      .subscribe((isValid) => {
        if (isValid) {
          this.userId = manageUserId;
          this.configureFreePayment();
        }
      });
  }

  /**
   * Gets the decoded manager user ID.
   *
   * @returns {null | number} the decoded manager user ID
   */
  private getManagerUserId(): null | number {
    if (this.params?.user_id) {
      return this.decodeUserId(this.params?.user_id);
    }

    return null;
  }

  /**
   * Adds Free to the ngx-payment enabled types.
   */
  private configureFreePayment(): void {
    this.configService.updatePaymentConfig({
      enabled: [...this.configService.paymentConfig().enabled, PaymentTypes.Free],
    });
  }

  /**
   * Validates the user ID.
   *
   * @param {number} userId the manager user ID
   *
   * @return {Observable<boolean>} an Observable that emits true if the user ID is valid, false otherwise
   */
  private validateUserId(userId: number): Observable<boolean> {
    return this.consultationRequestService.getManageUser(userId).pipe(
      map(() => true),
      catchError(() => of(false)),
    );
  }

  /**
   * Decodes the base64 manager user ID.
   *
   * @param {string} userId the encode manager user ID
   *
   * @returns {null | number} the decoded manager user ID or null if the decoding fails
   */
  private decodeUserId(userId: string): null | number {
    try {
      return parseInt(atob(userId));
    } catch (error) {
      this.errorLoggingService.logError(error);

      return null;
    }
  }

  /**
   * Sets up a listener to track form value changes and update the submit validation error message.
   */
  private setupFormValidationListener(): void {
    this.isFormValidationActive = true;
    this.resetFormValidationSubscription();
    this.consultationRequestService.consultationForm.markAllAsTouched();

    this.formValidationError = this.getFormValidationError();

    this.formValidationSubscription = this.consultationRequestService.consultationForm.valueChanges.subscribe(
      () => (this.formValidationError = this.getFormValidationError()),
    );
  }

  /**
   * Resets the form validation subscription.
   *
   * This ensures we always subscribe to the correct form components when payments change.
   */
  private resetFormValidationSubscription(): void {
    this.formValidationSubscription?.unsubscribe();
    this.formValidationSubscription = null;
  }
}
